import routeDirectory from './routes'

/**
 * Maps the entity kinds the backend uses to name an asset onto that asset's
 * detail page. Backend payloads (workflow notification link targets, the
 * `/r/{entity}/{id}` links baked into workflow emails and SMS) spell the kind
 * however the sender wrote it, so lookups go through {@link resolveEntityRoute}
 * rather than reading this table directly.
 *
 * A `Map` rather than an object literal because the kind is attacker-supplied
 * on an unauthenticated URL: an object lookup reaches inherited keys, so
 * `constructor` and `__proto__` would resolve to `Object` and `Object.prototype`
 * instead of to nothing.
 */
const ENTITY_ROUTES = new Map<string, (id: string) => string>([
  ['charger', routeDirectory.chargers.details],
  ['vehicle', routeDirectory.vehicles.details],
  ['site', routeDirectory.sites.details],
  ['battery', routeDirectory.batteries.details],
  ['generator', routeDirectory.generators.details],
  ['panel', routeDirectory.panels.details],
  ['link', routeDirectory.links.details]
])

/**
 * Resolves an entity kind and id to the asset's detail route, matching the
 * kind case-insensitively. Returns `undefined` for a kind with no detail page
 * and for a missing or empty id, so callers can degrade gracefully: plain text
 * instead of a broken link, an explanatory page instead of a redirect.
 *
 * Ids are free-form — a charge box id can hold spaces, `/`, `?`, or `#` — and
 * the route builders interpolate them verbatim, so the id is percent-encoded
 * here. Callers therefore pass the decoded id; Next.js hands the detail page
 * the decoded value back through its route params.
 */
export default function resolveEntityRoute(entity: unknown, entityId: unknown): string | undefined {
  if (typeof entity !== 'string' || typeof entityId !== 'string' || entityId.length === 0) return undefined
  return ENTITY_ROUTES.get(entity.toLowerCase())?.(encodeURIComponent(entityId))
}
